import { useTranslation } from 'react-i18next';
import { ActionsColumn as ActionsColumnCommon, ActionType } from '@otp/ui';
import { Archive, Clone, Configure, Order } from '@otp/icons';
import {
  Actions,
  AppArea,
  Subjects,
  WttpQuestionnaire,
  WttpQuestionnaireStatus,
} from 'src/graphql/generated/graphql';
import styles from './index.module.css';
import React from 'react';
import { ActionModals, EntityAction } from './actions-modals';
import { useCan } from 'src/utils/can';

type ActionsColumProps = {
  entity: WttpQuestionnaire;
  onActionFinished?: (action: EntityAction) => void;
};

export const ActionsColumn = ({
  entity,
  onActionFinished,
}: ActionsColumProps) => {
  const { t } = useTranslation();
  const [action, setAction] = React.useState<EntityAction>();
  const { can } = useCan();
  const canClone = can({
    action: Actions.Write,
    subject: Subjects.Questionnaires,
    appArea: AppArea.Wttp,
  });
  const canEdit = can({
    action: Actions.Write,
    subject: Subjects.Questionnaires,
    appArea: AppArea.Wttp,
  });
  const canSort = canEdit;
  const canArchive = canEdit;
  const canActivate = canEdit && entity.isActive;

  const actions = [
    canClone && {
      id: 'clone',
      label: t('common.clone'),
      icon: <Clone />,
      onClick: () => setAction(EntityAction.Clone),
    },
    canEdit && {
      id: 'edit',
      label: t('common.edit'),
      icon: <Configure />,
      onClick: () => setAction(EntityAction.Edit),
    },
    canSort && {
      id: 'sort',
      label: t('common.sort'),
      icon: <Order />,
      onClick: () => setAction(EntityAction.Sort),
    },
    canActivate && {
      id: 'activate',
      label:
        entity.status === WttpQuestionnaireStatus.Active
          ? t('common.set-as-draft')
          : t('common.activate'),
      icon: <Archive />,
      onClick: () => {
        const action =
          entity.status === WttpQuestionnaireStatus.Active
            ? EntityAction.Deactivate
            : EntityAction.Activate;
        setAction(action);
      },
    },
    canArchive && {
      id: 'archive',
      label: entity.isActive ? t('common.delete') : t('common.restore'),
      icon: <Archive />,
      onClick: () => {
        setAction(
          entity.isActive ? EntityAction.Archive : EntityAction.Unarchive,
        );
      },
    },
  ].filter(Boolean) as ActionType[];

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <ActionsColumnCommon
        actions={actions}
        classes={{ menu: styles.actionsColumn }}
      />
      {action && (
        <ActionModals
          id={entity.id}
          onClose={() => setAction(undefined)}
          action={action}
          onActionFinished={onActionFinished}
        />
      )}
    </div>
  );
};
